import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { DocumentSection, FactSheet } from "@/api/schemas";
import { refineSection } from "@/api/client";
import { useDocgenStore } from "./useDocgenStore";

interface RefineSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: DocumentSection;
  factSheet: FactSheet;
  /**
   * Sprint B — W5. When the user picks "Spoji ručno" from the
   * regen-diff sheet, we pre-stage the proposed content here so they
   * can read it side-by-side with the current text and craft a merge
   * instruction. Auto-seeds the textarea with a starter prompt.
   */
  proposedContent?: string | null;
}

/**
 * Slide-out panel for "AI dorada" — replaces the legacy inline
 * refine panel. Uses TanStack Query mutation so the loading + error
 * states are first-class.
 */
export function RefineSheet({
  open,
  onOpenChange,
  section,
  factSheet,
  proposedContent,
}: RefineSheetProps) {
  const [instruction, setInstruction] = useState("");
  const patchSection = useDocgenStore((s) => s.patchSection);

  const mutation = useMutation({
    mutationFn: () =>
      refineSection({
        section_id: section.section_id,
        current_content: section.content,
        instruction,
        fact_sheet: factSheet,
        section_type: section.section_type ?? "prose",
      }),
    onSuccess: (data) => {
      const refined = data.refined_content ?? data.content ?? data.text;
      if (typeof refined === "string" && refined.trim()) {
        patchSection(section.section_id, {
          content: refined,
          source: "llm_refined",
        });
        onOpenChange(false);
        setInstruction("");
      }
    },
  });

  useEffect(() => {
    if (!open) {
      mutation.reset();
      setInstruction("");
      return;
    }
    // Sprint B — W5. Seed a starter merge instruction when the sheet
    // opens with both texts available. The architect can edit/delete
    // this freely; it just removes the "blank textarea" friction
    // immediately after they bounced from the diff sheet.
    if (proposedContent && proposedContent.trim()) {
      setInstruction(
        "Spoji najbolje od oba teksta: zadrži strukturu trenutnog, ali preuzmi konkretnije formulacije iz prijedloga gdje je to prikladno.",
      );
    }
    // mutation reference is stable per render-pair — disabling deps to
    // avoid spurious re-resets while the user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full max-w-xl flex-col">
        <SheetHeader>
          <SheetTitle>AI dorada — {section.title}</SheetTitle>
          <SheetDescription>
            Opiši što želiš promijeniti. AI će zadržati strukturu i izvore, ali
            primijeniti tvoje smjernice.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-3 overflow-auto p-5">
          <div className="rounded-md border border-stone-100 bg-stone-50 p-3 text-[0.82rem] text-stone-600">
            <div className="mb-1 text-[0.7rem] font-bold uppercase tracking-[0.05em] text-stone-400">
              Trenutni tekst
            </div>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap font-sans">
              {section.content}
            </pre>
          </div>
          {proposedContent && (
            // Sprint B — W5. "Spoji ručno" entry from the regen-diff
            // sheet drops the AI prijedlog here so the architect can
            // hold both texts in view while writing the merge prompt.
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[0.82rem] text-emerald-900">
              <div className="mb-1 text-[0.7rem] font-bold uppercase tracking-[0.05em] text-emerald-700">
                Novi prijedlog (iz regeneracije)
              </div>
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap font-sans">
                {proposedContent}
              </pre>
            </div>
          )}
          <Textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={4}
            placeholder="npr. 'dodaj više detalja o parkiranju' ili 'skrati za jednu trećinu'"
          />
          {mutation.error instanceof Error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
              {mutation.error.message}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-stone-100 p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Odustani
          </Button>
          <Button
            disabled={!instruction.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <Sparkles className="h-4 w-4" />
            {mutation.isPending ? "Primjenjujem..." : "Primijeni"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
